import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getConversations } from '../services/conversations';
import { formatFirestoreError } from '../utils/firestoreErrors';
import ConversationCard from './ConversationCard';
import { useAuth } from '../context/AuthContext';
import Auth from './Auth';

interface ConversationListProps {
  onViewDetails: (conversationId: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ onViewDetails }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  
  const { data: conversations, isLoading, error } = useQuery(
    ['conversations', user?.uid],
    getConversations,
    {
      enabled: !!user,
      refetchInterval: 30000,
    }
  );

  // Filter and sort conversations
  const filteredConversations = useMemo(() => {
    if (!conversations) return [];

    let filtered = conversations;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = conversations.filter((conversation: any) => {
        // Search in title
        const titleMatch = conversation.title?.toLowerCase().includes(query);
        
        // Search in message content
        const messageMatch = conversation.messages?.some((message: any) => 
          message.content?.toLowerCase().includes(query)
        );
        
        return titleMatch || messageMatch;
      });
    }

    // Sort by created_at from earliest to latest
    return [...filtered].sort((a: any, b: any) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateA - dateB; // Earliest first
    });
  }, [conversations, searchQuery]);

  if (!user) {
    return (
      <div style={{
        backgroundColor: 'rgba(245, 245, 220, 0.05)',
        border: '1px solid rgba(245, 245, 220, 0.2)',
        borderRadius: 0,
        padding: '3rem',
        textAlign: 'center',
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#ffffff',
          marginBottom: '0.5rem',
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '0.05em',
        }}>
          SIGN IN TO VIEW CONVERSATIONS
        </h3>
        <p style={{ color: '#ffffff', marginBottom: '1.5rem', fontFamily: "'Inter', sans-serif" }}>
          Your conversations are stored privately in Firestore and linked to your account.
        </p>
        <button
          onClick={() => setShowAuth(true)}
          style={{
            background: '#ffffff',
            border: '1px solid #ffffff',
            color: '#000000',
            padding: '0.75rem 2rem',
            fontSize: '0.9rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            fontFamily: "'Inter', sans-serif",
            borderRadius: 0,
          }}
        >
          Login / Sign Up
        </button>
        {showAuth && <Auth onClose={() => setShowAuth(false)} />}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} style={{
            backgroundColor: 'rgba(245, 245, 220, 0.05)',
            border: '1px solid rgba(245, 245, 220, 0.2)',
            borderRadius: 0,
            padding: '1.5rem'
          }} className="animate-pulse">
            <div style={{ height: '1rem', backgroundColor: 'rgba(222, 184, 135, 0.3)', width: '25%', marginBottom: '1rem' }}></div>
            <div className="space-y-2">
              <div style={{ height: '0.75rem', backgroundColor: 'rgba(222, 184, 135, 0.3)', width: '75%' }}></div>
              <div style={{ height: '0.75rem', backgroundColor: 'rgba(222, 184, 135, 0.3)', width: '50%' }}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'rgba(245, 245, 220, 0.05)',
        border: '2px solid #ffffff',
        borderRadius: 0,
        padding: '1.5rem'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#ffffff',
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '0.05em'
        }}>
          Error loading conversations: {formatFirestoreError(error)}
        </div>
      </div>
    );
  }

  if (!conversations || conversations.length === 0) {
    return (
      <div style={{
        backgroundColor: 'rgba(245, 245, 220, 0.05)',
        border: '1px solid rgba(245, 245, 220, 0.2)',
        borderRadius: 0,
        padding: '3rem'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="w-16 h-16 mx-auto mb-4" style={{ color: '#ffffff' }}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#ffffff',
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '0.05em'
          }}>
            NO CONVERSATIONS YET
          </h3>
          <p style={{
            color: '#ffffff',
            marginBottom: '1.5rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            Upload your first conversation JSON file to get started with analysis.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'transparent',
              border: '2px solid #ffffff',
              color: '#ffffff',
              padding: '0.75rem 2rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.color = '#000000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#ffffff';
            }}
          >
            Upload Conversations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Search Input */}
      <div style={{
        backgroundColor: 'rgba(245, 245, 220, 0.05)',
        border: '1px solid rgba(245, 245, 220, 0.2)',
        borderRadius: 0,
        padding: '1.5rem'
      }}>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5" fill="none" stroke="#ffffff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="SEARCH CONVERSATIONS BY KEYWORD..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 3rem 0.75rem 2.5rem',
              backgroundColor: '#000000',
              border: '1px solid #ffffff',
              borderRadius: 0,
              color: '#ffffff',
              fontFamily: "'Inter', sans-serif",
              fontSize: '0.9rem',
              letterSpacing: '0.05em',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#ffffff'}
            onBlur={(e) => e.target.style.borderColor = '#ffffff'}
          />
          {searchQuery && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  color: '#ffffff',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#ffffff'}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
        {searchQuery && (
          <p style={{
            marginTop: '0.75rem',
            fontSize: '0.875rem',
            color: '#ffffff',
            letterSpacing: '0.05em',
            fontFamily: "'Inter', sans-serif"
          }}>
            Found {filteredConversations.length} result{filteredConversations.length !== 1 ? 's' : ''} (sorted from earliest to latest)
          </p>
        )}
      </div>

      {/* Results */}
      {filteredConversations.length === 0 ? (
        <div style={{
          backgroundColor: 'rgba(245, 245, 220, 0.05)',
          border: '1px solid rgba(245, 245, 220, 0.2)',
          borderRadius: 0,
          padding: '3rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="w-16 h-16 mx-auto mb-4" style={{ color: '#ffffff' }}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              NO CONVERSATIONS FOUND
            </h3>
            <p style={{
              color: '#ffffff',
              marginBottom: '1.5rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              Try adjusting your search keywords.
            </p>
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'transparent',
                border: '2px solid #ffffff',
                color: '#ffffff',
                padding: '0.75rem 2rem',
                fontSize: '0.9rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: "'Inter', sans-serif",
                borderRadius: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.color = '#000000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#ffffff';
              }}
            >
              Clear Search
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredConversations.map((conversation) => (
            <ConversationCard 
              key={conversation.id} 
              conversation={conversation} 
              onViewDetails={onViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationList;





